import { sumMoney, toMoneyNumber } from '@erp/shared-utils';
import {
  isBuyDesignated,
  selectCartPoLines,
  type CartMemberLine,
} from './lineFilters';
import { effectiveQuantity } from './lineQuantity';
import { lineWeightLbs } from './lineWeight';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

type TotalsLine = CartMemberLine &
  Pick<
    AssignmentLine,
    'neededQuantity' | 'calculatedQuantity' | 'unitWeightLbs'
  >;

export interface LineTotals {
  lineCount: number;
  totalQuantity: number;
  /** Extended cost of the purchase lines; a pure transfer carries none. */
  totalCost: number;
  /** Weight of the lines whose item has one; null when none does. */
  totalWeightLbs: number | null;
  /** Lines left out of the weight because the item has none on file. */
  unweighedLineCount: number;
}

/**
 * Totals for the lines in the cart: non-rejected and still selected, the same
 * membership the rail gauge and the Create summary use. Cost is summed as
 * money (cent-exact), not as floats.
 */
export function computeLineTotals(
  lines: readonly TotalsLine[],
  selectedIds: ReadonlySet<string>,
): LineTotals {
  const { selected } = selectCartPoLines([...lines], selectedIds);
  const weights = selected.map(lineWeightLbs);
  const known = weights.filter((w): w is number => w !== null);
  return {
    lineCount: selected.length,
    totalQuantity: selected.reduce(
      (sum, line) => sum + effectiveQuantity(line),
      0,
    ),
    totalCost: toMoneyNumber(
      sumMoney(
        selected.flatMap((line) =>
          isBuyDesignated(line) ? [line.topOption?.totalCost ?? 0] : [],
        ),
      ),
    ),
    totalWeightLbs:
      known.length > 0 ? known.reduce((sum, w) => sum + w, 0) : null,
    unweighedLineCount: weights.length - known.length,
  };
}
